import { useEffect, useRef, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { deselectEvent } from "../../redux/actions/events";
import { formatBalance, getInitials } from "../../helpers/functions";
import UserMenu from "./userMenu";
import { RevSkeleton } from "../ds";
import logoColor from "./revopic-logo-color.svg";
import { EVENTS_LIST_PATH, eventsSearchLocation, readEventsSearch } from "../../helpers/eventsSearch";

const Header = ({ user, userFailed, deselectEvent, location, history }) => {
    const { t, i18n } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);

    const isEventsActive = location?.pathname?.includes('/events');
    const isGuideActive = location?.pathname?.startsWith('/guide');
    const initials = getInitials(user?.name);
    const firstName = user?.name?.split(' ')[0] || '';
    const formattedBalance = formatBalance(user?.balance, i18n.language);
    // Misma guarda que el raíl: sin saldo en el store aún no ha llegado GET /user; si el
    // GET cayó (userFailed) se pinta el «—» en vez de seguir en esqueleto.
    const sinUsuario = user?.balance === undefined && !userFailed;

    // Buscador de eventos: filtra el listado de /events por nombre o código. El texto vive en
    // la URL (?q=): en el listado se sustituye la entrada del historial; desde cualquier otra
    // pantalla la primera tecla lleva al listado. Como la cabecera se remonta con cada ruta,
    // el foco se recupera vía location.state al aterrizar.
    const isEventsList = location?.pathname === EVENTS_LIST_PATH;
    const searchValue = isEventsList ? readEventsSearch(location) : '';
    const searchRef = useRef(null);

    useEffect(() => {
        if (location?.state?.focusSearch && searchRef.current) {
            const el = searchRef.current;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }
        // eslint-disable-next-line
    }, []);

    const handleSearchChange = (e) => {
        const q = e.target.value;
        const target = eventsSearchLocation(q);
        if (isEventsList) {
            history.replace(target);
        } else {
            deselectEvent();
            setMenuOpen(false);
            history.push({ ...target, state: { focusSearch: true } });
        }
    };

    const clearSearch = () => {
        history.replace(eventsSearchLocation(''));
        searchRef.current?.focus();
    };

    return (
        <>
            <header className="rev-cabecera" aria-busy={sinUsuario || undefined}>
                <div className="rev-movil">
                    <img src={logoColor} alt="Revopic" className="rev-movil__logo" />
                    <Link
                        to="/events"
                        className={`rev-capsula ${isEventsActive ? 'is-active' : ''}`}
                        onClick={() => { deselectEvent(); setMenuOpen(false); }}
                    >
                        {t('nav.rail.events')}
                    </Link>
                    <Link
                        to="/guide"
                        className={`rev-capsula ${isGuideActive ? 'is-active' : ''}`}
                        onClick={() => { deselectEvent(); setMenuOpen(false); }}
                    >
                        {t('nav.rail.guide')}
                    </Link>
                    <Link to="/account/credit/home" className="rev-capsula" onClick={() => setMenuOpen(false)}>
                        {sinUsuario
                            ? <span>{t('account.sideNavigation.credit')} · <RevSkeleton kind="cifra" /></span>
                            : <>{t('account.sideNavigation.credit')} · {formattedBalance}</>}
                    </Link>
                    <button
                        type="button"
                        className={`rev-capsula rev-capsula--cuenta ${menuOpen ? 'is-open' : ''}`}
                        onClick={() => setMenuOpen(o => !o)}
                    >
                        {sinUsuario
                            ? <RevSkeleton kind="avatar" className="rev-capsula__avatar" />
                            : <span className="rev-capsula__avatar">{initials}</span>}
                        {sinUsuario ? <RevSkeleton kind="texto" /> : firstName}
                    </button>
                </div>
                <div className="rev-cabecera__search" role="search">
                    <span className="material-icons rev-icon-sm" aria-hidden="true">search</span>
                    <input
                        ref={searchRef}
                        type="search"
                        className="rev-cabecera__search-input"
                        placeholder={t('nav.header.searchPlaceholder')}
                        aria-label={t('nav.header.searchPlaceholder')}
                        value={searchValue}
                        onChange={handleSearchChange}
                        autoComplete="off"
                    />
                    {searchValue && (
                        <button
                            type="button"
                            className="rev-cabecera__search-clear"
                            onClick={clearSearch}
                            aria-label={t('nav.header.searchClear')}
                        >
                            <span className="material-icons rev-icon-sm">close</span>
                        </button>
                    )}
                </div>
            </header>
            {menuOpen && (
                <div className="rev-movil-menu">
                    <UserMenu user={user} onNavigate={() => setMenuOpen(false)} />
                </div>
            )}
        </>
    );
};

const mapStateToProps = state => ({
    user: state.usersReducer.user,
    userFailed: state.usersReducer.userFailed
});

const mapDispatchToProps = {
    deselectEvent
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));
